import { useMemo, useState } from "react";
import type { APIResult } from "../api/client";
import { classNames, formatJSON } from "../lib/format";

type Props<T> = {
  result: APIResult<T> | null;
  loading?: boolean;
  /** Conteúdo customizado para mostrar acima do JSON bruto (ex.: cards de resumo). */
  header?: React.ReactNode;
  emptyHint?: string;
};

// Card padronizado para exibir a resposta de uma chamada de API:
// status, latência, copy-to-clipboard e JSON formatado.
export function ResponseCard<T>({ result, loading, header, emptyHint }: Props<T>) {
  const [copied, setCopied] = useState(false);
  const formatted = useMemo(() => {
    if (!result) return "";
    if (result.data !== null && result.data !== undefined) return formatJSON(result.data);
    return result.raw;
  }, [result]);

  function copy() {
    navigator.clipboard.writeText(formatted).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div className="ks-card">
      <div className="flex items-center justify-between border-b border-ink-200 px-4 py-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-ink-800">Resposta</span>
          {loading && (
            <span className="ks-tag bg-sky-100 text-sky-700">
              <span className="inline-block size-1.5 animate-pulse rounded-full bg-sky-600" />
              executando...
            </span>
          )}
          {result && !loading && (
            <>
              <span
                className={classNames(
                  "ks-tag",
                  result.ok
                    ? "bg-brand-100 text-brand-800"
                    : result.status >= 500
                      ? "bg-rose-100 text-rose-800"
                      : "bg-amber-100 text-amber-800",
                )}
              >
                HTTP {result.status}
              </span>
              <span className="ks-tag bg-ink-100 text-ink-700">{result.latencyMs} ms</span>
            </>
          )}
        </div>
        {result && (
          <button type="button" onClick={copy} className="ks-btn-ghost text-xs">
            {copied ? "copiado!" : "copiar"}
          </button>
        )}
      </div>
      {header && <div className="border-b border-ink-200 px-4 py-3">{header}</div>}
      <div className="ks-scroll max-h-[60vh] overflow-auto px-4 py-3">
        {!result && !loading && (
          <p className="text-sm text-ink-500">{emptyHint ?? "Nenhuma chamada feita ainda."}</p>
        )}
        {(result || loading) && (
          <pre className="ks-code whitespace-pre-wrap break-words text-ink-800">{formatted}</pre>
        )}
      </div>
    </div>
  );
}
